import React, { useContext, useState, useCallback, useMemo, memo } from "react";
import { ProductsContext } from "../context/products.context";
import LeftSidebar from "./LeftSidebar";
import OptionsOutsideSchool from "./OptionsOutsideSchool";
import ListComparisonDistricts from "./ListWorkforce copy 2";
import { ResponsiveLine } from "@nivo/line";
import { useCipChartData } from "../hooks/useCipChartData";
import Tippy from "@tippyjs/react";
import { followCursor, sticky } from "tippy.js";
import { motion } from "framer-motion";
import { area } from "d3-shape";
import { Defs } from "@nivo/core";
import "tippy.js/dist/tippy.css";
import { useCipModal } from "../hooks/useCipModal";
import CipModalDialog from "./CipModalDialog";

// Memoized CipChart component to prevent unnecessary re-renders
const CipChart = memo(({ cipcode, cipData, globalMax, districtName, comparisonSelection }) => {
  const [hoveredX, setHoveredX] = useState(null);
  const [hoveredSliceContent, setHoveredSliceContent] = useState(null);

  const { stateLine, districtLine, comparisonLine, offered, isNonTrad } = cipData;

  // Build Nivo data - memoize to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    return [
      {
        id: "Statewide",
        color: "#d3d3d345",
        data: stateLine,
      },
      {
        id: districtName,
        color: "#1249eeb7",
        data: districtLine,
      },
      {
        id: comparisonSelection,
        color: "#34d37ba8",
        data: comparisonLine,
      },
    ].filter((series) => series.data && series.data.length > 0);
  }, [stateLine, districtLine, comparisonLine, districtName, comparisonSelection]);

  // Tooltip handler
  const handleMouseEnter = useCallback((xCoord, margin, tooltipContent) => {
    setHoveredX(margin.left + xCoord);
    setHoveredSliceContent(tooltipContent);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredX(null);
    setHoveredSliceContent(null);
  }, []);

  // Custom slice layer with Tippy for each slice
  const customSliceLayer = useCallback(({ slices, height, xScale, margin }) => {
    return (
      <g>
        {slices.map((slice) => {
          const firstPoint = slice.points[0];
          const xCoord = xScale(firstPoint.data.x);
          const hitPadding = 70; // Increase vertical hit area
          const tooltipContent = (
            <div className="bg-white p-2 z-50 rounded-md shadow-md w-full border border-slate-200 text-black space-y-2 text-xs">
              {slice.points.map((point) => (
                <div key={point.id} className="">
                  <div className="flex flex-row items-center gap-2">
                    <div
                      style={{
                        background: point.serieColor,
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                      }}
                    />
                    <strong>{point.serieId}</strong>
                  </div>
                  <p className="pl-6">
                    {point.data.yFormatted} (
                    {point.data.num.toLocaleString()} of{" "}
                    {point.data.dum.toLocaleString()})
                  </p>
                </div>
              ))}
            </div>
          );
  
          return (
            <Tippy
              theme="clusterChartBlue"
              key={slice.id}
              content={tooltipContent}
              followCursor={true}
              plugins={[followCursor]}
              appendTo={document.body}
              interactive={false}
              arrow={false}
              offset={[15, 15]}
              placement="bottom-start"
            >
             <rect
                x={slice.x0}
                y={slice.y0 - (hitPadding/3)}
                width={slice.width}
                height={slice.height + hitPadding}
                fill="transparent"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => handleMouseEnter(xCoord, margin, tooltipContent)}
                onMouseLeave={handleMouseLeave}
              />
            </Tippy>
          );
        })}
  
        {hoveredX !== null && (
          <>
            {/* Crosshair line */}
            <motion.line
              style={{ pointerEvents: "none" }}
              y1={-50}
              y2={height -70}
              stroke="#000"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              initial={{
                x1: hoveredX - margin.left,
                x2: hoveredX - margin.left,
                opacity: 0,
              }}
              animate={{
                x1: hoveredX - margin.left,
                x2: hoveredX - margin.left,
                opacity: 1,
              }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 34,
              }}
            />
  
            {/* Floating tooltip */}
            <Tippy
              theme="clusterChartBlue"
              interactive={true}
              sticky={true}
              plugins={[sticky]}
              placement="top"
              offset={[0, 0]}
              content={hoveredSliceContent}
              visible={true}
            >
              <motion.div
                animate={{ left: hoveredX }}
                transition={{ type: "spring", stiffness: 180, damping: 40 }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: hoveredX,
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                }}
              />
            </Tippy>
          </>
        )}
      </g>
    );
  }, [hoveredX, hoveredSliceContent, handleMouseEnter, handleMouseLeave]);

  // AreaLayer for fill below the line - memoized
  const AreaLayer = useCallback(({ series, xScale, yScale, innerHeight }) => {
    if (!series || !series.length || !series[0].data) return null;
    
    // Filter out any data points with null or NaN values
    const validData = series[0].data.filter(d => 
      d && d.data && d.data.y !== null && !isNaN(d.data.y)
    );
    
    // Only proceed if we have valid data points
    if (validData.length === 0) return null;
    
    // Create the area generator with proper handling of missing values
    const pathGen = area()
      .x((d) => xScale(d.data.x))
      .y0(() => yScale(0))
      .y1((d) => {
        // Check for null or NaN values and return a valid y-coordinate
        const value = d.data.y;
        return value !== null && !isNaN(value) ? yScale(value) : yScale(0);
      })
      .defined(d => d.data.y !== null && !isNaN(d.data.y)); // Only include points with valid y values

    return (
      <>
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.7} />
            <stop offset="95%" stopColor="#9ca3af" stopOpacity={0.4} />
          </linearGradient>
        </defs>
        <Defs
          defs={[
            {
              id: "pattern",
              type: "patternLines",
              background: "transparent",
              color: "#3daff7",
              lineWidth: 1,
              spacing: 6,
              rotation: -45,
            },
          ]}
        />
        <path
          d={pathGen(validData)}
          fill="url(#colorUv)"
          fillOpacity={0.6}
        />
      </>
    );
  }, []);

  // Memoize chart layers to prevent recreation on each render
  const chartLayers = useMemo(() => [
    "markers",
    "areas",
    AreaLayer,
    "lines",
    customSliceLayer,
    "axes",
    "points",
  ], [AreaLayer, customSliceLayer]);

  // Pick background color based on whether this CIP is offered
  const chartBg = offered ? "bg-sky-50" : "bg-slate-50";

  return (
    <div
      className={chartBg}
      style={{ position: "relative", width: 320, height: 188 }}
    >
      <ResponsiveLine
        data={chartData}
        curve="linear"
        margin={{ top: 25, right: 25, bottom: 50, left: 25 }}
        yFormat=" >-.2%"
        axisTop={null}
        axisRight={null}
        axisLeft={null}
        enableSlices="x"
        layers={chartLayers}
        yScale={{
          max: globalMax,
          stacked: false,
          min: 0,
          type: "linear",
        }}
        colors={{ datum: "color" }}
        enableGridX={true}
        enableGridY={true}
        animate={true}
        pointSize={4}
        lineWidth={3}
        pointColor={{ from: "color", modifiers: [["brighter", "5"]] }}
        pointBorderColor={{ from: "serieColor" }}
        pointBorderWidth={2}
        pointLabelYOffset={-12}
        useMesh={true}
        enableArea={false} // Disable built-in area to use custom AreaLayer
        /* Props to handle missing data points */
        connectNulls={true} // Connect across null values
        missing={(d) => d.y === null || isNaN(d.y)} // Define what "missing" means
        theme={{
          crosshair: {
            line: {
              stroke: "#000000",
              strokeWidth: 1,
              strokeDasharray: "6 6",
            },
          },
          axis: {
            domain: {
              line: {
                stroke: "#9eb2d4",
                strokeWidth: 1,
              },
            },
            ticks: {
              line: {
                stroke: "#9eb2d4",
                strokeWidth: 1,
              },
              text: { fill: "#9eb2d4" },
            },
            legend: { text: { fontSize: 12 } },
          },
        }}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateX: 0,
            translateY: 50,
            itemsSpacing: 10,
            itemDirection: "left-to-right",
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: "square",
            symbolBorderColor: "rgba(0, 0, 0, .5)",
          },
        ]}
      />
    </div>
  );
});

// Memoized CipCard component
const CipCard = memo(({ code, data, isTotalsView, nontrad, globalMax, districtName, comparisonSelection, onHeaderClick }) => {
  // hide empty lines
  if (
    !data.stateLine.length &&
    !data.districtLine.length &&
    !data.comparisonLine.length
  )
    return null;

  return (
    <div
      key={code}
      className="flex flex-row h-[270px] mb-[100px] mx-3 cursor-pointer"
      onClick={() => onHeaderClick?.(code)}
    >
      <div
        className={`${
          data.offered ? "bg-sky-50" : "bg-slate-50"
        } relative rounded-t-xl group hover:border-sky-100 border-4 border-transparent`}
      >
        {/* ---------- Card Header ---------- */}
        <button className="flex w-[320px] h-[72px]">
          {/* Non‑Trad badge only in CIP view */}
          {!isTotalsView && data.isNonTrad && (
            <div
              title="Non Traditional"
              className="absolute -top-3 -left-2 w-7 h-7 rounded-full bg-indigo-100 border flex items-center justify-center text-indigo-600"
            >
              <span className="text-xs font-bold">NT</span>
            </div>
          )}
          <div className="inline-flex ml-4 mt-4 items-top">
            <div className="w-[5px] h-[18px] mx-2 mt-1 bg-[#3C43FF]" />
            <div>
              <p className="text-left w-[250px] text-slate-900 truncate">
                {data.cipName}
              </p>

              {/* CIP code + NT tag only when not totals view */}
              {!isTotalsView && (
                <p className="text-left text-slate-900">
                  {data.cipName !== code && `${code} `}
                  {nontrad?.female?.includes(code)
                    ? "(Non Traditional: Female)"
                    : nontrad?.male?.includes(code)
                    ? "(Non Traditional: Male)"
                    : ""}
                </p>
              )}
            </div>
          </div>
        </button>

        {/* ---------- Chart ---------- */}
        <CipChart 
          cipcode={code} 
          cipData={data} 
          globalMax={globalMax}
          districtName={districtName}
          comparisonSelection={comparisonSelection}
        />
      </div>
    </div>
  );
});

export const SchoolEnrolledGrid = () => {
  const {
    nontrad,
    districtName,
    filterSession,
    selectedSchools,
    comparisonTest,
    renton,
    stateData,
    comparisonSelection,
    setComparisonSelection,
  } = useContext(ProductsContext);

  const { isOpen, openModal, closeModal } = useCipModal();

  const isTotalsView = filterSession === "Program Area Totals";

  // Always call the hook so that React's rules are satisfied
  const { chartsByCip, globalMax, hasData } = useCipChartData({
    renton,
    stateData,
    comparisonTest,
    selectedSchools,
    filterSession,
    districtName,
    comparisonSelection,
    nontrad,
  });

  // Count non-traditional CIPs with enrollment data
  const nontradCounts = useMemo(() => {
    if (!renton || renton.length === 0) return { female: 0, male: 0 };

    // Get all CIP codes that have enrollment in the district
    const cipsWithEnrollment = new Set(
      renton
        .filter((item) =>
          item.enrolled?.some((e) =>
            selectedSchools.map((s) => s.value).includes(e.schoolName) &&
            e.enrollment?.some((v) => v > 0)
          )
        )
        .map((item) => item.cipcode)
    );

    return {
      female: nontrad.female.filter((cip) => cipsWithEnrollment.has(cip)).length,
      male: nontrad.male.filter((cip) => cipsWithEnrollment.has(cip)).length,
    };
  }, [renton, selectedSchools, nontrad]);

  // Memoize handler to prevent recreation on each render
  const handleChangeFilter = useCallback((newFilter) => {
    setComparisonSelection(newFilter);
  }, [setComparisonSelection]);

  // Memoize card entries to prevent recreation when unrelated state changes
  const cardEntries = useMemo(() => {
    const entries = Object.entries(chartsByCip);
    
    // Sort based on view type
    if (isTotalsView) {
      // Alphabetic by program‑area title
      return entries.sort(([_, a], [__, b]) => a.cipName.localeCompare(b.cipName));
    }
    
    // Sort by most recent statewide enrollment (descending)
    return entries.sort(([_, a], [__, b]) => {
      const aEnroll = a.stateLine[4]?.num || 0;
      const bEnroll = b.stateLine[4]?.num || 0;
      return bEnroll - aEnroll;
    });
  }, [chartsByCip, isTotalsView]);

  // If data is not yet available, render a loading state
  if (!hasData) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="max-w-[2000px] pt-4">
      <div className="mx-auto px-4">
        <div className="bg-white border-gray-100 border-r-2 fixed z-20 mt-10 inset-0 top-[3.8125rem] left-4 right-auto w-[19.5rem] pb-10 px-8 overflow-y-auto">
          <LeftSidebar nontradFemaleCount={nontradCounts.female} nontradMaleCount={nontradCounts.male} />
        </div>
        <div className="pl-[360px] bg-white ">
          <header className="items-center gap-6">
            <h1 className="mt-2 text-4xl font-bold text-slate-900">
              Proportion of CIP Enrollments within {filterSession}
            </h1>
            <h4 className="text-lg font-medium leading-8 text-slate-700">
              % of total CIP enrollments across {filterSession} vs. WA State (grey)
            </h4>
          </header>
          <div className="space-x-6">
            <ListComparisonDistricts changeFilter={handleChangeFilter} />
            <hr className="mb-12 mt-4 border-gray-200" />
          </div>
          <OptionsOutsideSchool isMulti closeMenuOnSelect={false} />
          <div className="flex flex-wrap mx-auto pt-10">
            {cardEntries.map(([code, data]) => (
              <CipCard
                key={code}
                code={code}
                data={data}
                isTotalsView={isTotalsView}
                nontrad={nontrad}
                globalMax={globalMax}
                districtName={districtName}
                comparisonSelection={comparisonSelection}
                onHeaderClick={openModal}
              />
            ))}
          </div>
        </div>
        <CipModalDialog isOpen={isOpen} onClose={closeModal} />
      </div>
    </div>
  );
};

export default SchoolEnrolledGrid;